import React, { useState, useMemo } from 'react'
import omit from 'lodash/omit'
import {
   Typography,
   Button,
   FormControl,
   InputLabel,
   Select,
   OutlinedInput,
   FormHelperText,
   MenuItem,
   Checkbox,
   ListItemText,
   Chip,
   TextField
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TooltipInputError from '../../toolTip/ToolTipInputError'
import Utils from 'utils/utils'
import TRANSLATE, { VALUES, REGEXP, OPERACIONES_OPERACION } from 'utils/constants'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useHasOperation } from 'hooks/useHasOperation'

// TODO llevar al constants
const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
   PaperProps: {
      style: {
         maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
         width: 250
      }
   }
}

const NO_MORE_THAN_150_CHARACTERS = Utils.noMoreThanNCharacters(150)
const NO_MORE_THAN_255_CHARACTERS = Utils.noMoreThanNCharacters(255)

const useStyles = makeStyles(theme => ({
   formContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '95%',
      margin: '0 auto 10px'
   },
   formControl: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: '14px',
      '&:not(:first-child)': {
         marginTop: '20px'
      }
   },
   formControlTextArea: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: '14px',
      '&:not(:first-child)': {
         marginTop: '20px'
      }
   },
   button: {
      width: '100%',
      height: '40px',
      marginTop: '20px',
      color: 'white',
      background: '#3264DC'
   },
   formCleanButton: {
      color: '#3264DC',
      border: '1px solid #3264DC',
      fontWeight: 'bold',
      marginTop: 20,
      width: '100%'
   },
   helperText: {
      marginLeft: '0px',
      marginRight: '0px',
      marginTop: '0px',
      fontSize: '11px'
   },
   selectEmpty: {
      marginTop: theme.spacing(2)
   },
   disabledButton: {
      backgroundColor: '#12B3F9!important',
      opacity: '0.3',
      color: '#fff!important'
   },
   MuiFormHelperText: {
      root: {
         margin: '0!important'
      },
      margin: '0!important'
   },
   chips: {
      display: 'flex',
      flexWrap: 'wrap'
   },
   chip: {
      margin: 2
   },
   multipleSelectLabel: {
      backgroundColor: 'white'
   },
   titleOne: {
      margin: '20px 0 15px 9px',
      fontStyle: 'normal',
      fontSize: 14,
      alignSelf: 'start',
      color: '#8A8FAB',
      fontWeight: 400
   }
}))

const INITIAL_OPERATION_FORM_VALIDATION = {
   name: false,
   description: false,
   types: false,
   status: false,
   functionality: false,
   application: false,
   module: false
}

const validRequired = value => !value || value === ''

const validationsFields = {
   name: value => value.length > 151 || validRequired(value),
   description: value => value.length > 255 || validRequired(value),
   types: value => value.length === 0,
   functionality: validRequired,
   status: validRequired,
   application: validRequired,
   module: validRequired
}

const OperationForm = ({
   formData,
   setFormData,
   functionalities,
   actionSubmit,
   formBehavior,
   cleanCallback,
   applications,
   getFunctionalitiesByModuleId,
   getModulesByApplicationId,
   modules,
   setModules,
   setFunctionalities
}) => {
   const classes = useStyles()

   const [invalid, setInvalid] = useState(INITIAL_OPERATION_FORM_VALIDATION)

   const isSearch = useMemo(
      () => formBehavior === VALUES.FORM_BEHAVIOR.SEARCH,
      [formBehavior]
   )

   const CONSULTAR_OPERACION = useHasOperation(OPERACIONES_OPERACION.CONSULTAR)
   const REGISTRAR_OPERACION = useHasOperation(OPERACIONES_OPERACION.REGISTRAR)

   function cleanForm() {
      cleanCallback()
      setInvalid(INITIAL_OPERATION_FORM_VALIDATION)
   }

   const validateFields = (name, value) => {
      const isInvalidFn =
         name in validationsFields ? validationsFields[name] : () => {}
      const isInvalid = isInvalidFn(value)
      if (isInvalid) {
         setInvalid(inv => ({
            ...inv,
            [name]: true
         }))
      } else if (invalid[name]) {
         setInvalid(inv => ({
            ...inv,
            [name]: false
         }))
      }
   }

   function isApplicationEmpty() {
      return validRequired(formData.application)
   }

   function isModuleEmpty() {
      return validRequired(formData.module)
   }

   const addValuesToMemory = e => {
      const { name, value } = e.target
      if (!isSearch) {
         validateFields(name, value)
      } else if (isSearch && name === 'module') {
         validateFields('module', value)
      } else if (isSearch && name === 'functionality') {
         validateFields('functionality', value)
      }
      setFormData(prevFormData => ({
         ...prevFormData,
         [name]: value
      }))
   }

   const registerOperations = type => {
      const newValidations = {}
      const formFields =
         type === VALUES.FORM_BEHAVIOR.REGISTER
            ? omit(INITIAL_OPERATION_FORM_VALIDATION, 'status')
            : INITIAL_OPERATION_FORM_VALIDATION
      let isInvalidField = false

      for (const key in formFields) {
         const isInvalid = validationsFields[key](formData[key])
         newValidations[key] = isInvalid
         if (isInvalid && !isInvalidField) {
            isInvalidField = isInvalid
         }
      }
      if (isInvalidField) {
         setInvalid(prev => {
            return { ...prev, ...newValidations }
         })
      } else {
         actionSubmit(type)
      }
   }

   const actionSubmitEvent = e => {
      e.preventDefault()
      switch (formBehavior) {
         case VALUES.FORM_BEHAVIOR.REGISTER: {
            registerOperations(VALUES.FORM_BEHAVIOR.REGISTER)
            break
         }
         case VALUES.FORM_BEHAVIOR.SEARCH: {
            if (formData.application !== null && formData.module === null) {
               setInvalid(prev => {
                  return { ...prev, module: true }
               })
               break
            }
            if (formData.module !== null && formData.functionality === null) {
               setInvalid(prev => {
                  return { ...prev, functionality: true }
               })
               break
            }
            actionSubmit(VALUES.FORM_BEHAVIOR.SEARCH)
            break
         }
         case VALUES.FORM_BEHAVIOR.EDIT: {
            registerOperations(VALUES.FORM_BEHAVIOR.EDIT)
            break
         }
         default:
            break
      }
   }

   function cleanModule() {
      addValuesToMemory({
         target: {
            name: 'module',
            value: ''
         }
      })
      setInvalid(prev => {
         return { ...prev, module: false }
      })
      setModules([])
   }

   function cleanFunctionality() {
      addValuesToMemory({
         target: {
            name: 'functionality',
            value: ''
         }
      })
      setInvalid(prev => {
         return { ...prev, functionality: false }
      })
      setFunctionalities([])
   }

   function onApplicationChange(e, newValue) {
      addValuesToMemory({
         target: {
            name: 'application',
            value: newValue
         }
      })
      if (newValue && newValue.id) {
         getModulesByApplicationId(newValue.id)
      } else {
         cleanModule()
         cleanFunctionality()
      }
   }

   function onModuleChange(e, newValue) {
      addValuesToMemory({
         target: {
            name: 'module',
            value: newValue
         }
      })
      if (newValue && newValue.id) {
         getFunctionalitiesByModuleId(newValue.id)
      } else {
         cleanFunctionality()
      }
   }

   return (
      <div className={classes.formContainer}>
         <Typography className={classes.titleOne}>
            <b> Completa los campos </b>{' '}
         </Typography>
         <form className={classes.formContainer}>
            <FormControl
               variant="outlined"
               size="small"
               className={classes.formControl}
               error={invalid.name}
            >
               <InputLabel htmlFor="outlined-adornment-amount">
                  {`Nombre de Operación ${!isSearch ? '*' : ''}`}
               </InputLabel>
               <OutlinedInput
                  error={invalid.name}
                  value={formData.name}
                  onChange={Utils.convertInputValueToUpperCase(
                     addValuesToMemory
                  )}
                  name="name"
                  size="small"
                  label="Nombre de Operación  "
                  inputProps={{ maxLength: 151 }}
                  endAdornment={
                     (invalid.name && (
                        <TooltipInputError title={'ingrese un nombre valido'} />
                     )) ||
                     (formData.name.length === 151 && (
                        <TooltipInputError
                           title={NO_MORE_THAN_150_CHARACTERS}
                        />
                     ))
                  }
               />
               {invalid.name && (
                  <FormHelperText className={classes.helperText}>
                     {!formData.name
                        ? 'El campo Nombre de Operación es obligatorio'
                        : 'ingrese un nombre valido'}
                  </FormHelperText>
               )}
            </FormControl>

            {!isSearch && (
               <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.formControlTextArea}
                  error={invalid.description}
               >
                  <InputLabel htmlFor="outlined-adornment-amount">
                     {`Descripción de la Operación ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <OutlinedInput
                     error={invalid.description}
                     value={formData.description}
                     onChange={addValuesToMemory}
                     label="Descripción de la Operación  "
                     name="description"
                     endAdornment={
                        (invalid?.description && (
                           <TooltipInputError
                              title={'ingrese una descripcion valida'}
                           />
                        )) ||
                        (formData?.description?.length === 255 && (
                           <TooltipInputError
                              title={NO_MORE_THAN_255_CHARACTERS}
                           />
                        ))
                     }
                     multiline
                     rows={4}
                     inputProps={{ maxLength: 255 }}
                  />
                  <FormHelperText className={classes.helperText}>
                     {invalid.description &&
                        'El campo Descripción de la Operación es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            <FormControl
               variant="outlined"
               size="small"
               className={classes.formControl}
               margin="dense"
               error={invalid.types}
            >
               <InputLabel
                  htmlFor="mutiple-checkbox-outlined-label"
                  className={classes.multipleSelectLabel}
               >
                  {`Tipo de Operación ${!isSearch ? '*' : ''}`}
               </InputLabel>
               <Select
                  labelId="mutiple-checkbox-outlined-label"
                  id="types"
                  multiple
                  value={formData.types}
                  label="Tipo de Operación  "
                  onChange={event => {
                     setFormData(old => ({
                        ...old,
                        types: event.target.value
                     }))
                     if (!isSearch) {
                        validateFields('types', event.target.value)
                     }
                  }}
                  renderValue={selected => (
                     <div className={classes.chips}>
                        {selected.map(item => (
                           <Chip
                              key={item.value}
                              label={item.label}
                              className={classes.chip}
                           />
                        ))}
                     </div>
                  )}
                  MenuProps={MenuProps}
                  error={invalid.types}
               >
                  {VALUES.OPERATION_TYPES.map(option => (
                     <MenuItem key={option.value} value={option}>
                        <Checkbox
                           color="primary"
                           checked={
                              (formData.types || [])
                                 .map(item => item.value)
                                 .indexOf(option.value) !== -1
                           }
                        />
                        <ListItemText primary={option.label} />
                     </MenuItem>
                  ))}
               </Select>
               <FormHelperText className={classes.helperText}>
                  {invalid.types &&
                     'Seleccione al menos un tipo de operación válido'}
               </FormHelperText>
            </FormControl>

            <FormControl
               size="small"
               variant="outlined"
               className={classes.formControl}
               error={invalid.application}
            >
               <Autocomplete
                  size="small"
                  options={applications}
                  getOptionLabel={option => option.name}
                  renderOption={option => (
                     <span key={option.id}>{option.name}</span>
                  )}
                  value={formData.application}
                  onChange={onApplicationChange}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={`Nombre de Aplicación ${!isSearch ? '*' : ''}`}
                        variant="outlined"
                        value={''}
                        error={invalid.application}
                     />
                  )}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.application &&
                     'El campo Nombre de la Aplicación es obligatorio'}
               </FormHelperText>
            </FormControl>
            <FormControl
               size="small"
               variant="outlined"
               className={classes.formControl}
               error={invalid.module}
            >
               <Autocomplete
                  size="small"
                  options={modules}
                  getOptionLabel={option => option.name}
                  renderOption={option => (
                     <span key={option.id}>{option.name}</span>
                  )}
                  value={formData.module}
                  onChange={onModuleChange}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={`Nombre del Módulo ${!isSearch ? '*' : ''}`}
                        variant="outlined"
                        error={invalid.module}
                     />
                  )}
                  disabled={isApplicationEmpty()}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.module &&
                     'El campo Nombre del Módulo es obligatorio'}
               </FormHelperText>
            </FormControl>

            <FormControl
               size="small"
               variant="outlined"
               className={classes.formControl}
               error={invalid.functionality}
            >
               <Autocomplete
                  size="small"
                  options={functionalities}
                  getOptionLabel={option => option.name}
                  renderOption={option => (
                     <span key={option.id}>{option.name}</span>
                  )}
                  value={formData.functionality}
                  onChange={(e, newValue) => {
                     addValuesToMemory({
                        target: {
                           name: 'functionality',
                           value: newValue
                        }
                     })
                  }}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={`Nombre de Funcionalidad ${
                           !isSearch ? '*' : ''
                        }`}
                        variant="outlined"
                        value={''}
                        error={invalid.functionality}
                     />
                  )}
                  disabled={isModuleEmpty()}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.functionality &&
                     'El campo Nombre de Funcionalidad es obligatorio'}
               </FormHelperText>
            </FormControl>
            {formBehavior !== VALUES.FORM_BEHAVIOR.REGISTER && (
               <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  size="small"
                  error={invalid.status}
               >
                  <InputLabel htmlFor="outlined-type-native-simple">
                     {`Estado de la Operación ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <Select
                     value={formData.status}
                     onChange={addValuesToMemory}
                     size="small"
                     name="status"
                     label="Estado de la Operación  "
                     inputProps={{
                        name: 'status',
                        id: 'outlined-type-native-simple'
                     }}
                  >
                     <MenuItem aria-label="None" value={''} />
                     {VALUES.STATUS_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                           {option.label}
                        </MenuItem>
                     ))}
                  </Select>
                  <FormHelperText className={classes.helperText}>
                     {invalid.status &&
                        'El campo Estado de la Operación es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            <Button
               type="submit"
               fullWidth
               variant="contained"
               color="primary"
               className={classes.button}
               onClick={actionSubmitEvent}
               disabled={
                  formBehavior === VALUES.FORM_BEHAVIOR.SEARCH ? 
                  !CONSULTAR_OPERACION : !REGISTRAR_OPERACION}
            >
               {formBehavior === VALUES.FORM_BEHAVIOR.EDIT
                  ? TRANSLATE.BUTTONS.EDIT
                  : isSearch
                  ? TRANSLATE.BUTTONS.SEARCH
                  : TRANSLATE.BUTTONS.SAVE}
            </Button>
            <Button
               classes={{
                  root: classes.formCleanButton
               }}
               variant="outlined"
               onClick={cleanForm}
            >
               {TRANSLATE.BUTTONS.CLEAN}
            </Button>
         </form>
      </div>
   )
}

export default OperationForm
